import React, { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import Modal, { PrevButton } from '@jarvis.oddle/core/components/Modal'
import { Text } from '@jarvis.oddle/core/components/Typo'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'
import { paddingSizes } from '@jarvis.oddle/core/theme/default'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import IconClose from '@jarvis.oddle/core/components/Icons/IconClose'
import Flex from '@jarvis.oddle/core/components/Flex'
import { createCalendarEvent } from 'src/components/molecules/NewOrderDetails/helper'
import { buttonStyle } from '@jarvis.oddle/core/components/Button/Button'
import useTracker from 'src/hooks/useTracker'

const Container = styled(Modal.FlexContainer).attrs(() => ({
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  padding: paddingSizes.medium
}))`
  position: relative;
  background: ${(props) => props.theme.colorContainerColor};
  width: 95%;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  transform: translateX(-50%) translateY(-50%);
  top: 50%;
  left: 50%;
  height: fit-content;
  ${breakpoint('desktop')`
    width: 100%;
    max-width: 600px;
    height: fit-content;
  `}
`

const CloseButtonContainer = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  role: 'button'
}))`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 10;
`

const StyledHeader = styled(Text)`
  margin-top: 50px;
  margin-bottom: 20px;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  color: ${(props) => props.theme.colorHeadingsFont};
`

const StyledContent = styled(Text)`
  margin-top: 10px;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
`

const StyledButtonLink = styled.a`
  ${buttonStyle};
  margin-top: 20px;
  width: 100%;
  height: 45px;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledButtonText = styled(Text)`
  color: ${(props) => props.buttonTextColor};
  font-size: 18px;
  font-weight: 700;
`

const StyledPrevButton = styled(PrevButton)`
  position: absolute;
  top: 30px;
  right: 10px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 10;
`

interface DownloadCalendarFileModalProps {
  modal: {
    show: boolean
    onClose: (x: () => void) => void
  }
  storeOrder: Oms.V4.StoreOrder
  publicUrl: string
}

const DownloadCalendarFileModal = ({
  storeOrder,
  modal,
  publicUrl
}: DownloadCalendarFileModalProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const theme = useContext(ThemeContext)
  const { segmentTrackerOrderReminderSaved } = useTracker()

  return (
    <Modal {...modal} show={modal.show} slideInAnimation='right'>
      <Container>
        <CloseButtonContainer onClick={modal.onClose}>
          <IconClose fill='currentColor' width={14} height={14} />
        </CloseButtonContainer>
        <StyledPrevButton
          onClick={() =>
            modal.onClose(() =>
              modalController.showModal('SelectCalendarModal')
            )
          }
        >
          <PrevButton data-testid='backButton' />
        </StyledPrevButton>
        <StyledHeader>{t('Download Calendar File')}</StyledHeader>
        <StyledContent>
          {t(
            'Save the calendar file to your device and open the file with a compatible application.'
          )}
        </StyledContent>
        <StyledButtonLink
          {...theme}
          href={`${
            storeOrder ? createCalendarEvent({ storeOrder, publicUrl, t }) : ''
          }`}
          download='calendar.ics'
          onClick={() =>
            modal.onClose(() => {
              segmentTrackerOrderReminderSaved({
                ...storeOrder,
                orderReminderDestination: 'Calendar File'
              })
              modalController.showModal('SuccessDownloadCalendarModal', {
                storeOrder,
                publicUrl
              })
            })
          }
        >
          <StyledButtonText
            buttonTextColor={theme.buttonTextColor}
            data-testid='confirmDownloadCalendarFileButton'
          >
            {t('Ok')}
          </StyledButtonText>
        </StyledButtonLink>
      </Container>
    </Modal>
  )
}

export default makeControlledModal(
  DownloadCalendarFileModal,
  'DownloadCalendarFileModal'
)
